import {
	Add01Icon,
	AlertCircleIcon,
	Delete02Icon,
	Download01Icon,
	PencilEdit01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Head, Link, useForm } from "@inertiajs/react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import {
	type Conflict,
	ConflictDialog,
	findConflicts,
	mergeValues,
} from "@/components/conflict-dialog";
import { type Country, CountrySelect } from "@/components/country-select";
import Dashboard from "@/components/dashboard";
import type { EditingPerson } from "@/components/editing-person-item";
import { LegalFormAutocomplete } from "@/components/legal-form-autocomplete";
import { type Person, PersonForm } from "@/components/person-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { formatBirthNumber } from "@/lib/formatters";
import type HomeUser from "@/types/serializers/HomeUser";

interface Props {
	user: HomeUser;
	countries: Country[];
	legal_forms: string[];
}

interface FormData {
	name: string;
	ico: string;
	legal_form: string | null;
	street: string | null;
	city: string | null;
	postal_code: string | null;
	country: string | null;
	persons: Person[];
}

const ENTITY_FIELD_LABELS: Record<string, string> = {
	name: "Název společnosti",
	legal_form: "Právní forma",
	street: "Ulice a číslo popisné",
	city: "Město",
	postal_code: "PSČ",
	country: "Země",
};

function getPersonErrors(
	errors: Partial<Record<string, string>>,
	index: number,
): Record<string, string> {
	const prefix = `persons.${index}.`;
	const result: Record<string, string> = {};
	for (const [key, value] of Object.entries(errors)) {
		if (key.startsWith(prefix) && value) {
			result[key.slice(prefix.length)] = value;
		}
	}
	return result;
}

function hasPersonErrors(
	errors: Partial<Record<string, string>>,
	index: number,
): boolean {
	const prefix = `persons.${index}.`;
	return Object.keys(errors).some((k) => k.startsWith(prefix));
}

function formatRoles(p: Person): string {
	const roles: string[] = [];
	if (p.is_ubo) roles.push("Skutečný majitel");
	if (p.is_statutory_member) roles.push("Člen stat. orgánu");
	if (p.other_role) roles.push(p.other_role);
	return roles.join(", ");
}

export default function NewBusiness({ user, countries, legal_forms }: Props) {
	// editingIndex: null = closed, -1 = adding new, >= 0 = editing at index
	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	const [editingPersons, setEditingPersons] = useState(
		new Map<string, EditingPerson>(),
	);
	const [pending, setPending] = useState<{
		conflicts: Conflict[];
		mergedValues: Partial<FormData>;
		persons: Person[];
		description: string;
	}>();

	const { data, setData, post, processing, errors, clearErrors, transform } =
		useForm<FormData>({
			name: "",
			ico: "",
			legal_form: null,
			street: null,
			city: null,
			postal_code: null,
			country: "CZ",
			persons: [],
		});

	const defaults: Partial<FormData> = { country: "CZ" };

	const applyOrShowConflicts = (
		entityValues: Partial<FormData>,
		newPersons: Person[],
	) => {
		const mergedValues = mergeValues(data, entityValues);
		const conflicts: Conflict[] = findConflicts(
			data,
			entityValues,
			ENTITY_FIELD_LABELS,
			defaults,
		);

		const formatFullName = ({ physical_person: p }: Person) => {
			return `${p.first_name} ${p.last_name}`;
		};

		if (data.persons.length > 0) {
			conflicts.push({
				field: "persons",
				label: "Osoby",
				oldValue: data.persons.map(formatFullName).join(", "),
				newValue: newPersons.map(formatFullName).join(", "),
			});
		}

		if (conflicts.length === 0) {
			setData({
				...data,
				...mergedValues,
				persons: newPersons,
			});
		} else {
			setPending({
				conflicts,
				mergedValues,
				persons: newPersons,
				description: "Údaje byly načteny z ARES.",
			});
		}
	};

	const handleApply = () => {
		if (!pending) return;
		setData({
			...data,
			...pending.mergedValues,
			persons: pending.persons,
		});
		setPending(undefined);
	};

	const handleCancel = () => {
		setPending(undefined);
	};

	const aresMutation = useMutation({
		mutationFn: async (ico: string) => {
			const response = await fetch(`/ares/lookup_vr?ico=${ico}`);
			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Nepodařilo se načíst data z ARES");
			}
			return response.json();
		},
		onSuccess: (result) => {
			const entity = result.entity;
			const statutoryMembers = result.statutory_members || [];

			const entityValues: Partial<FormData> = {
				name: entity.name || data.name,
				legal_form: entity.legal_form || data.legal_form,
				street: entity.street || data.street,
				city: entity.city || data.city,
				postal_code: entity.postal_code || data.postal_code,
				country: entity.country || data.country,
			};

			const newPersons: Person[] = statutoryMembers.map(
				(member: {
					first_name: string;
					last_name: string;
					street?: string;
					city?: string;
					postal_code?: string;
					country?: string;
					is_statutory_member?: boolean;
					is_ubo?: boolean;
				}) => ({
					_key: crypto.randomUUID(),
					is_ubo: member.is_ubo ?? false,
					is_statutory_member: member.is_statutory_member ?? true,
					other_role: null,
					physical_person: {
						first_name: member.first_name || "",
						last_name: member.last_name || "",
						birth_number: "",
						street: member.street || null,
						city: member.city || null,
						postal_code: member.postal_code || null,
						country: member.country || "CZ",
						document_type: null,
						document_number: null,
						document_expiry_date: null,
						document_issuer: null,
						birth_date: null,
						birth_place: null,
						birth_country: "CZ",
						citizenship: null,
						citizenship_other: null,
						gender: null,
						is_sanctioned: null,
						is_pep: null,
						pep_info: null,
					},
				}),
			);

			applyOrShowConflicts(entityValues, newPersons);
		},
		onError: (error: Error) => {
			alert(error.message);
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		transform((formData) => ({
			...formData,
			persons: formData.persons.map((p) => ({
				...p,
				physical_person: {
					...p.physical_person,
					id: editingPersons.get(p._key)?.id ?? null,
					birth_number: p.physical_person.birth_number || null,
				},
			})),
		}));
		post("/clients/create_business");
	};

	const addPerson = () => {
		setEditingIndex(-1);
	};

	const editPerson = (index: number) => {
		setEditingIndex(index);
	};

	const savePerson = (person: Person, editingPerson?: EditingPerson) => {
		if (editingPerson) {
			setEditingPersons((prev) => {
				const next = new Map(prev);
				next.set(person._key, editingPerson);
				return next;
			});
		} else {
			setEditingPersons((prev) => {
				const next = new Map(prev);
				next.delete(person._key);
				return next;
			});
		}

		if (editingIndex !== null && editingIndex >= 0) {
			const updated = [...data.persons];
			updated[editingIndex] = person;
			setData("persons", updated);

			// const personErrorKeys = Object.keys(errors).filter((k) =>
			// 	k.startsWith(`persons.${editingIndex}.`),
			// );
			// if (personErrorKeys.length > 0) {
			// 	clearErrors(...(personErrorKeys as [string, ...string[]]));
			// }
		} else {
			setData("persons", [...data.persons, person]);
		}
		setEditingIndex(null);
	};

	const removePerson = (index: number) => {
		setData(
			"persons",
			data.persons.filter((_, i) => i !== index),
		);
	};

	const getEditingPerson = (): Person | null => {
		if (editingIndex === null) return null;
		if (editingIndex === -1) {
			return {
				_key: crypto.randomUUID(),
				physical_person: {
					first_name: "",
					last_name: "",
					birth_number: "",
					street: null,
					city: null,
					postal_code: null,
					country: "CZ",
					document_type: null,
					document_number: null,
					document_expiry_date: null,
					document_issuer: null,
					birth_date: null,
					birth_place: null,
					birth_country: "CZ",
					citizenship: null,
					citizenship_other: null,
					gender: null,
					is_sanctioned: null,
					is_pep: null,
					pep_info: null,
				},
				is_ubo: false,
				is_statutory_member: false,
				other_role: null,
			};
		}
		return data.persons[editingIndex];
	};

	const getInitialEditingPerson = (): EditingPerson | undefined => {
		if (editingIndex === null || editingIndex < 0) return undefined;
		return editingPersons.get(data.persons[editingIndex]._key);
	};

	const currentPerson = getEditingPerson();

	return (
		<>
			<Head title="Nový klient - Právnická osoba" />
			<Dashboard user={user}>
				<form
					onSubmit={handleSubmit}
					className="flex flex-1 flex-col overflow-hidden"
				>
					<div className="overflow-y-auto p-4 lg:p-6">
						<div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
							<div>
								<h2 className="font-semibold text-2xl">
									Nový klient – Právnická osoba
								</h2>
								<p className="text-muted-foreground text-sm">
									Vyplňte údaje o novém klientovi. Povinná pole jsou označena{" "}
									<span className="text-destructive">*</span>.
								</p>
							</div>

							<Card>
								<CardHeader>
									<CardTitle>
										IČO <span className="text-destructive">*</span>
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="flex gap-2">
										<Field className="flex-1" data-invalid={!!errors.ico}>
											<Input
												id="ico"
												value={data.ico}
												onChange={(e) => {
													clearErrors("ico");
													setData("ico", e.target.value);
												}}
												placeholder="12345678"
												aria-invalid={!!errors.ico}
											/>
											<FieldError>{errors.ico}</FieldError>
										</Field>
										<Button
											type="button"
											variant="outline"
											onClick={() => aresMutation.mutate(data.ico)}
											disabled={!data.ico || aresMutation.isPending}
										>
											<HugeiconsIcon
												icon={Download01Icon}
												strokeWidth={2}
												data-icon="inline-start"
											/>
											{aresMutation.isPending ? "Načítám..." : "Načíst z ARES"}
										</Button>
									</div>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>Údaje o společnosti</CardTitle>
								</CardHeader>
								<CardContent>
									<FieldGroup>
										<Field data-invalid={!!errors.name}>
											<FieldLabel htmlFor="name">
												Název společnosti{" "}
												<span className="text-destructive">*</span>
											</FieldLabel>
											<Input
												id="name"
												value={data.name}
												onChange={(e) => {
													clearErrors("name");
													setData("name", e.target.value);
												}}
												placeholder="Společnost s.r.o."
												aria-invalid={!!errors.name}
											/>
											<FieldError>{errors.name}</FieldError>
										</Field>
										<Field>
											<FieldLabel htmlFor="legal_form">Právní forma</FieldLabel>
											<LegalFormAutocomplete
												value={data.legal_form || ""}
												onChange={(value) =>
													setData("legal_form", value || null)
												}
												legalForms={legal_forms}
											/>
										</Field>
									</FieldGroup>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>Sídlo</CardTitle>
								</CardHeader>
								<CardContent>
									<FieldGroup>
										<Field data-invalid={!!errors.street}>
											<FieldLabel htmlFor="street">
												Ulice a číslo popisné
											</FieldLabel>
											<Input
												id="street"
												value={data.street || ""}
												onChange={(e) => {
													clearErrors("street");
													setData("street", e.target.value || null);
												}}
												placeholder="Ulice 123/4"
												aria-invalid={!!errors.street}
											/>
											<FieldError>{errors.street}</FieldError>
										</Field>
										<div className="flex gap-4">
											<Field className="w-full" data-invalid={!!errors.city}>
												<FieldLabel htmlFor="city">Město</FieldLabel>
												<Input
													id="city"
													value={data.city || ""}
													onChange={(e) => {
														clearErrors("city");
														setData("city", e.target.value || null);
													}}
													placeholder="Praha"
													aria-invalid={!!errors.city}
												/>
												<FieldError>{errors.city}</FieldError>
											</Field>
											<Field
												className="w-68"
												data-invalid={!!errors.postal_code}
											>
												<FieldLabel htmlFor="postal_code">PSČ</FieldLabel>
												<Input
													id="postal_code"
													value={data.postal_code || ""}
													onChange={(e) => {
														clearErrors("postal_code");
														setData("postal_code", e.target.value || null);
													}}
													placeholder="110 00"
													aria-invalid={!!errors.postal_code}
												/>
												<FieldError>{errors.postal_code}</FieldError>
											</Field>
											<Field className="w-68" data-invalid={!!errors.country}>
												<FieldLabel htmlFor="country">Země</FieldLabel>
												<CountrySelect
													value={data.country || ""}
													onChange={(value) => {
														clearErrors("country");
														setData("country", value);
													}}
													countries={countries}
												/>
												<FieldError>{errors.country}</FieldError>
											</Field>
										</div>
									</FieldGroup>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<div className="flex items-center justify-between gap-2">
										<CardTitle>Osoby</CardTitle>
										<Button type="button" variant="outline" onClick={addPerson}>
											<HugeiconsIcon
												icon={Add01Icon}
												strokeWidth={2}
												data-icon="inline-start"
											/>
											Přidat osobu
										</Button>
									</div>
								</CardHeader>
								<CardContent>
									{data.persons.length > 0 ? (
										<div className="rounded-xl border">
											<Table>
												<TableHeader>
													<TableRow>
														<TableHead>Jméno</TableHead>
														<TableHead>Rodné číslo</TableHead>
														<TableHead>Role</TableHead>
														<TableHead className="w-24">Akce</TableHead>
													</TableRow>
												</TableHeader>
												<TableBody>
													{data.persons.map((p, index) => (
														<TableRow
															key={p._key}
															className={`cursor-pointer ${
																hasPersonErrors(errors, index)
																	? "bg-destructive/10"
																	: ""
															}`}
															onClick={() => editPerson(index)}
														>
															<TableCell className="font-medium">
																<span className="flex items-center gap-1.5">
																	{p.physical_person.first_name}{" "}
																	{p.physical_person.last_name}
																	{hasPersonErrors(errors, index) && (
																		<HugeiconsIcon
																			icon={AlertCircleIcon}
																			strokeWidth={2}
																			className="size-4 text-destructive"
																		/>
																	)}
																</span>
															</TableCell>
															<TableCell>
																{formatBirthNumber(
																	p.physical_person.birth_number,
																) || (
																	<span className="text-muted-foreground">
																		—
																	</span>
																)}
															</TableCell>
															<TableCell>{formatRoles(p)}</TableCell>
															<TableCell>
																<div className="flex gap-1">
																	<Button
																		type="button"
																		variant="ghost"
																		size="icon-sm"
																		onClick={(e) => {
																			e.stopPropagation();
																			editPerson(index);
																		}}
																	>
																		<HugeiconsIcon
																			icon={PencilEdit01Icon}
																			strokeWidth={2}
																		/>
																	</Button>
																	<Button
																		type="button"
																		variant="ghost"
																		size="icon-sm"
																		onClick={(e) => {
																			e.stopPropagation();
																			removePerson(index);
																		}}
																	>
																		<HugeiconsIcon
																			icon={Delete02Icon}
																			strokeWidth={2}
																		/>
																	</Button>
																</div>
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</div>
									) : (
										<div className="rounded-xl border py-8 text-center text-muted-foreground text-sm">
											Zatím nebyly přidány žádné osoby. ARES neobsahuje skutečné
											majitele - přidejte je ručně.
										</div>
									)}
								</CardContent>
							</Card>

							{pending && (
								<ConflictDialog
									conflicts={pending.conflicts}
									description={pending.description}
									onApply={handleApply}
									onCancel={handleCancel}
								/>
							)}
						</div>
					</div>

					<div className="border-t bg-background px-4 py-4 lg:px-6">
						<div className="mx-auto flex w-full max-w-2xl">
							<div className="ml-auto flex gap-4">
								<Button
									type="button"
									variant="outline"
									size="lg"
									render={<Link href="/clients" />}
								>
									Zrušit
								</Button>
								<Button type="submit" size="lg" disabled={processing}>
									Uložit klienta
								</Button>
							</div>
						</div>
					</div>
				</form>

				<Sheet
					open={editingIndex !== null}
					onOpenChange={(open) => {
						if (!open) setEditingIndex(null);
					}}
				>
					<SheetContent
						side="right"
						className="gap-0 overflow-y-auto data-[side=right]:sm:max-w-2xl"
					>
						<SheetHeader className="border-b">
							<SheetTitle>
								{editingIndex !== null && editingIndex >= 0
									? "Upravit osobu"
									: "Přidat osobu"}
							</SheetTitle>
							<SheetDescription>
								Vyplňte údaje o osobě spojené s právnickou osobou.
							</SheetDescription>
						</SheetHeader>
						{currentPerson && (
							<PersonForm
								person={currentPerson}
								countries={countries}
								errors={
									editingIndex !== null && editingIndex >= 0
										? getPersonErrors(errors, editingIndex)
										: undefined
								}
								initialEditingPerson={getInitialEditingPerson()}
								mode="create"
								onSave={savePerson}
								onCancel={() => setEditingIndex(null)}
								onClearError={(field) => {
									if (editingIndex !== null && editingIndex >= 0) {
										clearErrors(
											`persons.${editingIndex}.physical_person.${field}`,
										);
									}
								}}
							/>
						)}
					</SheetContent>
				</Sheet>
			</Dashboard>
		</>
	);
}
